import React, { ReactNode, useEffect, useState } from 'react';
import { isUserLoggedIn as selectIsUserLoggedIn } from 'redux/modules/sessions/selectors';
import DeliveryPassStatusContext, {
  DeliveryPassStatusEnum,
} from 'components/BookSlot/contexts/DeliveryPassStatus/DeliveryPassStatusContext';
import { setIsDeliveryPassActive } from 'redux/modules/slot-booking/actions/set-is-delivery-pass-active';
import { apiCallback } from 'api';
import { useWtrStore, useWtrSelector, useWtrDispatch } from 'redux/hooks';

/**
 * Wraps the delivery-pass-orchestration pass status api so it's called once with access to
 * the data in multiple places with minimal Redux ties.
 */
export const DeliveryPassStatusProvider = ({ children }: { children: ReactNode }) => {
  const store = useWtrStore();
  const dispatch = useWtrDispatch();
  const [deliveryPassStatusContextValue, setDeliveryPassStatusContextValue] =
    useState<DeliveryPassStatusEnum>(DeliveryPassStatusEnum.UNKNOWN);
  const isUserLoggedIn = useWtrSelector(selectIsUserLoggedIn);

  useEffect(() => {
    if (isUserLoggedIn) {
      const fetchData = async () => {
        const apiCall = apiCallback(
          {
            service: 'delivery-pass-orchestration',
            endpoint: 'pass/status',
            verbs: ['get'],
            defaultVersion: 1,
          },
          'get',
        )({ iHandleStatusCodes: true });

        let data;
        try {
          data = await apiCall(store);
        } catch {
          // Ignore any errors and leave status as default which is currently "UNKNOWN".
          // and the desired experience is to not show anything and allow the user to continue.
        }

        if (data && 'status' in data) {
          dispatch(setIsDeliveryPassActive(data.status === DeliveryPassStatusEnum.ACTIVE));
          setDeliveryPassStatusContextValue(data.status);
        }
      };
      fetchData();
    }
  }, [dispatch, isUserLoggedIn, store]);

  return (
    <DeliveryPassStatusContext.Provider value={deliveryPassStatusContextValue}>
      {children}
    </DeliveryPassStatusContext.Provider>
  );
};
